import useTranslation from '../useTranslation';
import type { SiteWithParcelAndClientResponse } from 'generatedTypes/client/models';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { parseToItemsWithKeys } from 'utils/parseToItems';

import type { Props as DetailsViewEntityLinkProps } from 'components/DetailsViewEntityLink/types';
import type { DetailsViewEntityMenuItem } from 'components/DetailsViewEntityMenu/types';
import type { BoundaryPartData, Geometry } from 'components/Map/types';
import type { BoundaryFieldSchema } from 'components/fields/BoundarySelectPanelField/types';

import useApplications from 'hooks/useApplications';
import type { ApplicationResponse } from 'hooks/useApplications/types';
import useBoundaries from 'hooks/useBoundaries';
import type { BoundaryResponse } from 'hooks/useBoundaries/types';
import useBoundary from 'hooks/useBoundary';
import useChildCreation from 'hooks/useChildCreation';
import useField from 'hooks/useField';
import usePageRoutes from 'hooks/usePageRoutes';
import type { PaginatedGenerator } from 'hooks/usePagination/types';

import {
    DETAIL_TYPE_CLIENT,
    DETAIL_TYPE_FARM,
    DETAIL_TYPE_PRIMARY_BOUNDARY
} from 'pages/FieldDetailsPage/constants';

const useData = () => {
    const { fieldId } = useParams();
    const { EDIT_FIELD } = useTranslation();
    const { fetchField, isLoading } = useField();
    const { boundaryDetailsPage, clientDetailsPage, farmDetailsPage, fieldsPage } = usePageRoutes();
    const {
        applications,
        fetch: fetchApplications,
        isLoading: isApplicationsLoading
    } = useApplications();
    const { fetchBoundary } = useBoundary();
    const navigate = useNavigate();
    const [currentField, setCurrentField] = useState<SiteWithParcelAndClientResponse | undefined>(
        undefined
    );
    const [isApplicationsOpen, setIsApplicationsOpen] = useState(false);
    const [isLinkedEntitiesOpen, setIsLinkedEntitiesOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = useState<string | undefined>(
        undefined
    );
    const [selectedBoundaryGeometry, setSelectedBoundaryGeometry] = useState<Geometry | undefined>(
        undefined
    );
    const isFirstLoad = useRef(true);
    const {
        boundaries,
        fetch: fetchBoundaries,
        isLoading: isBoundariesLoading,
        reset: resetBoundaries,
        totalCount: totalBoundariesCount
    } = useBoundaries();
    const boundariesGenerator = useRef<PaginatedGenerator<BoundaryResponse> | null>(null);

    const childCreation = useChildCreation({ parentData: currentField, parentType: 'field' });

    // The primary boundary is identified from the loaded pages rather than the field response,
    // which carries no boundary data. The API returns primaries first, so it is always on the
    // first page of this site-scoped fetch.
    const primaryBoundary = (boundaries ?? []).find(({ isPrimary }) => isPrimary);
    const associatedBoundaries = (boundaries ?? []).filter(({ isPrimary }) => !isPrimary);

    // usePagination leaves data undefined until the first response lands, which is what tells the
    // edit form its boundary defaults are ready.
    const hasLoadedBoundaries = boundaries !== undefined;

    // The edit form reuses this fetch rather than repeating it.
    const boundaryFormItems: BoundaryFieldSchema[] = parseToItemsWithKeys(associatedBoundaries, [
        'isPrimary',
        'siteId',
        'siteName'
    ]);

    const primaryBoundaryFormItem: BoundaryFieldSchema | null = primaryBoundary
        ? parseToItemsWithKeys([primaryBoundary], ['isPrimary', 'siteId', 'siteName'])[0]
        : null;

    const primaryBoundaryLink: DetailsViewEntityLinkProps = primaryBoundary
        ? {
              entityName: primaryBoundary.name ?? '',
              entityRoute: boundaryDetailsPage(primaryBoundary.id ?? ''),
              entityType: DETAIL_TYPE_PRIMARY_BOUNDARY
          }
        : {
              // The entry stays so the label still reads, but the value is left blank.
              entityName: '',
              entityType: DETAIL_TYPE_PRIMARY_BOUNDARY
          };

    const entitiesLink: DetailsViewEntityLinkProps[] = [
        {
            entityName: currentField?.clientName ?? '',
            entityRoute: clientDetailsPage(currentField?.clientId ?? ''),
            entityType: DETAIL_TYPE_CLIENT
        },
        {
            entityName: currentField?.parcelName ?? '',
            entityRoute: farmDetailsPage(currentField?.parcelId ?? ''),
            entityType: DETAIL_TYPE_FARM
        },
        primaryBoundaryLink
    ];

    const boundaryMenuItems: DetailsViewEntityMenuItem[] = associatedBoundaries.map(
        ({ id = '', name = '' }) => ({
            entityId: id,
            entityName: name,
            entityRoute: boundaryDetailsPage(id)
        })
    );

    const associatedBoundariesCount = Math.max(totalBoundariesCount - (primaryBoundary ? 1 : 0), 0);

    const getCurrentField = async () => {
        if (fieldId) {
            const field = await fetchField({ id: fieldId });

            // Without the reset the paginated generator sees it already holds data and returns
            // without calling the API, so edits made in the form would not show up here.
            resetBoundaries();

            boundariesGenerator.current = fetchBoundaries({ siteId: fieldId });
            await boundariesGenerator.current.next();

            setCurrentField(field ?? undefined);
            fetchApplications({ siteId: fieldId }).next();
        }
    };

    useEffect(() => {
        if (fieldId) {
            getCurrentField();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldId]);

    const handleSelectApplication = async (application: ApplicationResponse) => {
        setSelectedApplicationId(application.id);

        if (!application.boundaryId) {
            setSelectedBoundaryGeometry(undefined);

            return;
        }

        const boundary = await fetchBoundary({ id: application.boundaryId });

        setSelectedBoundaryGeometry(
            boundary
                ? { isEditable: false, parts: boundary.boundaryParts as BoundaryPartData[] }
                : undefined
        );
    };

    useEffect(() => {
        if (applications && applications.length > 0 && isFirstLoad.current) {
            isFirstLoad.current = false;
            handleSelectApplication(applications[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applications]);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleToggleApplications = () => {
        setIsApplicationsOpen(previous => !previous);
    };

    const handleToggleLinkedEntities = () => {
        setIsLinkedEntitiesOpen(previous => !previous);
    };

    const handleEditClose = () => {
        if (isEditing) {
            setIsEditing(false);
            setIsClosing(false);
            getCurrentField();
        }
    };

    const dropdownMenuItems = [
        {
            onClick: handleEditClick,
            text: EDIT_FIELD
        },
        ...(currentField ? [childCreation.menuItem] : [])
    ];

    const handleNavigateBack = () => {
        navigate(fieldsPage);
    };

    const handleClose = () => {
        if (isEditing) {
            setIsClosing(true);
        } else {
            handleNavigateBack();
        }
    };

    const handleBoundaryNext = async () => {
        if (!boundariesGenerator.current) {
            return { done: true };
        }

        return boundariesGenerator.current.next();
    };

    return {
        applications,
        associatedBoundariesCount,
        boundaryFormItems,
        boundaryMenuItems,
        currentField,
        dropdownMenuItems,
        entitiesLink,
        handleBoundaryNext,
        handleClose,
        handleEditClose,
        handleSelectApplication,
        handleToggleApplications,
        handleToggleLinkedEntities,
        hasLoadedBoundaries,
        isApplicationsLoading,
        isApplicationsOpen,
        isBoundariesLoading,
        isClosing,
        isEditing,
        isLinkedEntitiesOpen,
        isLoading,
        primaryBoundaryFormItem,
        selectedApplicationId,
        selectedBoundaryGeometry,
        setIsClosing
    };
};

export default useData;
